import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { DateTimePicker, renderTimeViewClock } from "@mui/x-date-pickers";
import { useEffect, useState } from "react";
import DateTimePickerActionBar from "components/utils/DatePickerActionBar";
import { dateISOFormat, getUserFullName } from "helpers";
import useCrud from "hooks/useCrud";
import { useToast } from "hooks/useToast";
import { useSession } from "providers/SessionProvider";
import { Movement, Store, UpdateMovementArticlePayload, UpdateMovementPayload } from "types";
import { DATE_VIEWS, MOVEMENT_TYPES, STATUS_CODES } from "../../constants";
import { MOVEMENTS_URL } from "constants/urls";

type EditMovementModalProps = {
  open: boolean;
  onClose: () => void;
  movement: Movement;
  stores: Store[];
  onUpdate: (movement: Movement) => void;
};

const normalizeMovementObservationFields = (movement: Movement): Movement => ({
  ...movement,
  articles: Array.isArray(movement.articles)
    ? movement.articles.map((article) => {
        if (!article.article_locations) {
          return article;
        }

        const availableObservation =
          article.article_locations.observation_available ?? null;
        const notAvailableObservation =
          article.article_locations.observation_not_available ?? null;

        return {
          ...article,
          article_locations: {
            ...article.article_locations,
            observation_available: availableObservation,
            observation_not_available: notAvailableObservation,
          },
        };
      })
    : movement.articles,
});

export default function EditMovementModal({
  open,
  onClose,
  movement,
  stores,
  onUpdate,
}: EditMovementModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editableMovement, setEditableMovement] = useState<Movement>(
    normalizeMovementObservationFields(movement),
  );
  const { users } = useSession();
  const {
    updateData,
    showToast,
    toastMessage,
    toastSeverity,
    setShowToast,
  } = useCrud<Movement>(MOVEMENTS_URL, { disableAutoFetch: true });
  const { showSnackBar } = useToast();

  const isFieldEditable = (field: keyof Movement) => {
    const typeId = movement.movement_type_id;

    if (["client_document", "observation", "numero_remito"].includes(field)) {
      return typeId === MOVEMENT_TYPES.INCOME.id;
    }

    if (["pallets", "weight"].includes(field)) {
      return (
        typeId === MOVEMENT_TYPES.INCOME.id ||
        typeId === MOVEMENT_TYPES.DISCHARGE.id ||
        typeId === MOVEMENT_TYPES.PREPARATION.id ||
        typeId === MOVEMENT_TYPES.INCOME_ADJUSTMENT.id ||
        typeId === MOVEMENT_TYPES.DISCHARGE_ADJUSTMENT.id
      );
    }

    return false;
  };

  const isArticleLocationFieldEditable = (field: "observation"): boolean => {
    const typeId = movement.movement_type_id;

    return (
      field === "observation" &&
      (typeId === MOVEMENT_TYPES.INCOME.id ||
        typeId === MOVEMENT_TYPES.DISCHARGE.id ||
        typeId === MOVEMENT_TYPES.PREPARATION.id ||
        typeId === MOVEMENT_TYPES.INCOME_ADJUSTMENT.id ||
        typeId === MOVEMENT_TYPES.DISCHARGE_ADJUSTMENT.id)
    );
  };

  const isArticleFieldEditable = (
    _field: keyof Movement["articles"][0],
  ): boolean => {
    return false;
  };

  const getStoreDescription = (storeId: number | null): string => {
    const store = stores.find((item) => item.id === storeId);
    return store?.description ?? "";
  };

  const handleChange = <K extends keyof Movement>(field: K, value: Movement[K]) => {
    setEditableMovement((prev) => ({ ...prev, [field]: value }));
  };

  const handleChangeArticle = <K extends keyof Movement["articles"][0]>(
    index: number,
    field: K,
    value: Movement["articles"][0][K],
  ) => {
    setEditableMovement((prev) => {
      const nextArticles = [...prev.articles];
      nextArticles[index] = { ...nextArticles[index], [field]: value };
      return { ...prev, articles: nextArticles };
    });
  };

  const handleChangeArticleLocationObservation = (
    index: number,
    field: "observation_available" | "observation_not_available",
    value: string,
  ) => {
    setEditableMovement((prev) => {
      const nextArticles = [...prev.articles];
      const currentArticle = nextArticles[index];

      nextArticles[index] = {
        ...currentArticle,
        article_locations: {
          ...currentArticle.article_locations,
          [field]: value,
        },
      };

      return { ...prev, articles: nextArticles };
    });
  };

  const handleSubmit = async () => {
    if (!editableMovement.id) return;

    setIsSubmitting(true);

    try {
      const { date, ...restMovement } = editableMovement;

      const articlesPayload: UpdateMovementArticlePayload[] = editableMovement.articles
        .map((article): UpdateMovementArticlePayload | null => {
          if (typeof article.id !== "number") return null;

          const articleLocationId =
            article.article_locations_id ?? article.article_locations?.id ?? undefined;
          const availableObservation =
            article.article_locations?.observation_available;
          const notAvailableObservation =
            article.article_locations?.observation_not_available;

          return {
            id: article.id,
            lote: article.lote ?? null,
            oc: article.oc ?? null,
            ...(article.fecha_vencimiento
              ? { fecha_vencimiento: dateISOFormat(article.fecha_vencimiento) }
              : { fecha_vencimiento: null }),
            ...(articleLocationId ? { article_locations_id: articleLocationId } : {}),
            ...(availableObservation !== undefined ||
            notAvailableObservation !== undefined
              ? {
                  article_locations: {
                    observation_available: availableObservation ?? null,
                    observation_not_available: notAvailableObservation ?? null,
                  },
                }
              : {}),
          };
        })
        .filter((item): item is UpdateMovementArticlePayload => item !== null);

      const sanitizedMovement: UpdateMovementPayload = {
        ...restMovement,
        ...(date ? { date: dateISOFormat(date) } : {}),
        articles: articlesPayload,
      };

      const response = await updateData(
        `${editableMovement.id}/with-articles`,
        sanitizedMovement as any,
        true,
        true,
      );

      if (response?.status === STATUS_CODES.OK) {
        const [updatedMovement] = response.data;
        onUpdate(updatedMovement);
        onClose();
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (open) {
      setEditableMovement(normalizeMovementObservationFields(movement));
    }
  }, [movement, open]);

  useEffect(() => {
    if (showToast) {
      showSnackBar(toastMessage, toastSeverity);
      setShowToast(false);
    }
  }, [setShowToast, showSnackBar, showToast, toastMessage, toastSeverity]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
      <DialogTitle>Editar Movimiento</DialogTitle>
      <DialogContent>
        <Grid container columnSpacing={2} rowSpacing={2}>
          <Grid item xs={4}>
            <TextField
              label="Usuario"
              fullWidth
              disabled
              value={getUserFullName(movement.created_by, users)}
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              label="Preparo"
              fullWidth
              disabled
              value={getUserFullName(movement.created_by, users)}
            />
          </Grid>
          <Grid item xs={4}>
            <DateTimePicker
              label="Fecha"
              format="dd/MM/yyyy"
              views={DATE_VIEWS}
              value={editableMovement.date ? new Date(editableMovement.date) : null}
              onChange={() => {}}
              disabled
              slotProps={{ textField: { fullWidth: true } }}
              viewRenderers={{
                hours: renderTimeViewClock,
                minutes: renderTimeViewClock,
                seconds: renderTimeViewClock,
              }}
              slots={{ actionBar: DateTimePickerActionBar }}
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              label="Cliente"
              fullWidth
              disabled
              value={movement.client?.razon_social ?? ""}
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              label="Cliente Asociado"
              fullWidth
              disabled
              value={movement.associated_client ?? ""}
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              label="Documento del Cliente"
              fullWidth
              value={editableMovement.client_document ?? ""}
              onChange={(event) =>
                handleChange("client_document", event.target.value)
              }
              disabled={!isFieldEditable("client_document")}
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              label="Almacen"
              fullWidth
              disabled
              value={getStoreDescription(movement.store_id)}
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              label="Pallets"
              fullWidth
              type="number"
              value={editableMovement.pallets}
              onChange={(event) =>
                handleChange("pallets", Number(event.target.value))
              }
              disabled={!isFieldEditable("pallets")}
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              label="Peso"
              fullWidth
              type="number"
              value={editableMovement.weight}
              onChange={(event) =>
                handleChange("weight", Number(event.target.value))
              }
              disabled={!isFieldEditable("weight")}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="Numero de Remito"
              fullWidth
              value={editableMovement.numero_remito ?? ""}
              onChange={(event) =>
                handleChange("numero_remito", event.target.value)
              }
              disabled={!isFieldEditable("numero_remito")}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="Observaciones"
              fullWidth
              value={editableMovement.observation ?? ""}
              onChange={(event) => handleChange("observation", event.target.value)}
              disabled={!isFieldEditable("observation")}
            />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="body2" fontWeight={600} gutterBottom>
              Productos
            </Typography>
            <TableContainer component={Paper} elevation={4} sx={{ p: 1 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Código</TableCell>
                    <TableCell>Descripción</TableCell>
                    <TableCell>Cantidad</TableCell>
                    <TableCell>Ubicación</TableCell>
                    <TableCell>Cant. por Contenedor</TableCell>
                    <TableCell>Lote</TableCell>
                    <TableCell>Orden de Compra</TableCell>
                    <TableCell>Fecha de Vencimiento</TableCell>
                    <TableCell>Obs. (art. disp.)</TableCell>
                    <TableCell>Obs. (art. no disp.)</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {editableMovement.articles.map((article, index) => (
                    <TableRow hover key={index}>
                      <TableCell>{article.code}</TableCell>
                      <TableCell>{article.description}</TableCell>

                      <TableCell>
                        <TextField
                          type="number"
                          value={article.amount ?? ""}
                          size="small"
                          disabled
                        />
                      </TableCell>

                      <TableCell>
                        <TextField
                          type="string"
                          value={article.article_locations?.location?.code ?? ""}
                          size="small"
                          disabled
                        />
                      </TableCell>

                      <TableCell>
                        <TextField
                          type="number"
                          value={article.amount_per_container ?? ""}
                          size="small"
                          disabled
                        />
                      </TableCell>

                      <TableCell>
                        <TextField
                          value={article.article_locations?.lote ?? ""}
                          onChange={(event) =>
                            isArticleFieldEditable("lote") &&
                            handleChangeArticle(index, "lote", event.target.value)
                          }
                          size="small"
                          disabled={!isArticleFieldEditable("lote")}
                        />
                      </TableCell>

                      <TableCell>
                        <TextField
                          value={article.article_locations?.oc ?? ""}
                          onChange={(event) =>
                            isArticleFieldEditable("oc") &&
                            handleChangeArticle(index, "oc", event.target.value)
                          }
                          size="small"
                          disabled={!isArticleFieldEditable("oc")}
                        />
                      </TableCell>

                      <TableCell>
                        <DateTimePicker
                          value={
                            article.article_locations?.fecha_vencimiento
                              ? new Date(
                                  article.article_locations.fecha_vencimiento,
                                )
                              : null
                          }
                          onChange={(newValue) =>
                            isArticleFieldEditable("fecha_vencimiento") &&
                            handleChangeArticle(
                              index,
                              "fecha_vencimiento",
                              newValue ? newValue.toISOString() : undefined,
                            )
                          }
                          disabled={!isArticleFieldEditable("fecha_vencimiento")}
                          format="dd/MM/yyyy"
                          slotProps={{ textField: { size: "small", fullWidth: true } }}
                          viewRenderers={{
                            hours: renderTimeViewClock,
                            minutes: renderTimeViewClock,
                            seconds: renderTimeViewClock,
                          }}
                          slots={{ actionBar: DateTimePickerActionBar }}
                          views={DATE_VIEWS}
                        />
                      </TableCell>

                      <TableCell>
                        <TextField
                          value={article.article_locations?.observation_available ?? ""}
                          size="small"
                          fullWidth
                          multiline
                          minRows={1}
                          maxRows={3}
                          disabled
                        />
                      </TableCell>

                      <TableCell>
                        <TextField
                          value={
                            article.article_locations?.observation_not_available ?? ""
                          }
                          size="small"
                          fullWidth
                          multiline
                          minRows={1}
                          maxRows={3}
                          disabled
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        </Grid>
      </DialogContent>

      <EditMovementModalActions
        onClose={onClose}
        onSubmit={handleSubmit}
        isSubmitting={isSubmitting}
      />
    </Dialog>
  );
}

function EditMovementModalActions({
  onClose,
  onSubmit,
  isSubmitting,
}: {
  onClose: () => void;
  onSubmit: () => void;
  isSubmitting: boolean;
}) {
  return (
    <DialogActions>
      <Button onClick={onClose}>Cancelar</Button>
      <Button onClick={onSubmit} variant="contained" disabled={isSubmitting}>
        {isSubmitting ? "Guardando..." : "Guardar cambios"}
      </Button>
    </DialogActions>
  );
}
